import { ActionIcon, AppShell, Box, Burger, Drawer, Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconLogout } from '@tabler/icons-react';
import { NavLink as RouterNavLink, Outlet } from 'react-router-dom';
import { useLottie } from 'lottie-react';
import happyDog from '../assets/animations/happy-dog.json';
import profile from '../assets/animations/profile.json';
import { ROUTES, type NavGroupKey, type RouteMeta } from '../app/routes';
import { tokens } from '../app/theme';
import { useAuth } from '../features/auth/context';
import { ROLE_LABELS } from '../features/auth/api';

const GROUP_LABELS: Partial<Record<NavGroupKey, string>> = {
  content: '콘텐츠 관리',
  resource: '리소스 관리',
};

function SideLink({ to, title, icon: Icon, onNavigate }: RouteMeta & { onNavigate?: () => void }) {
  return (
    <RouterNavLink to={to} end={to === '/'} style={{ textDecoration: 'none' }} onClick={onNavigate}>
      {({ isActive }) => (
        <Group
          gap="sm"
          wrap="nowrap"
          px="sm"
          py={8}
          style={{
            borderRadius: 'var(--mantine-radius-sm)',
            borderLeft: `3px solid ${isActive ? tokens.accent.main : 'transparent'}`,
            background: isActive ? tokens.accent.soft : 'transparent',
            color: isActive ? tokens.accent.main : tokens.text.mid,
            boxShadow: isActive ? `0 0 12px ${tokens.accent.soft}` : 'none',
          }}
        >
          <Icon size={18} stroke={1.7} color={isActive ? tokens.accent.main : tokens.text.mid} />
          <Text size="sm" fw={isActive ? 600 : 400} c="inherit">
            {title}
          </Text>
        </Group>
      )}
    </RouterNavLink>
  );
}

function BandGroup({ groupKey, onNavigate }: { groupKey: NavGroupKey; onNavigate?: () => void }) {
  const { role } = useAuth();
  // roles 지정 항목은 현재 role이 포함될 때만 노출(미지정=전체).
  const items = ROUTES.filter(
    (r) => r.group === groupKey && (!r.roles || (role != null && r.roles.includes(role))),
  );
  return (
    <Box
      p="sm"
      style={{ background: tokens.surface.band, borderRadius: 'var(--mantine-radius-lg)' }}
    >
      <Text
        ff="monospace"
        size="xs"
        c={tokens.text.lo}
        tt="uppercase"
        mb={8}
        px="sm"
        style={{ letterSpacing: '0.12em' }}
      >
        {GROUP_LABELS[groupKey]}
      </Text>
      <Stack gap={4}>
        {items.map((r) => (
          <SideLink key={r.to} {...r} onNavigate={onNavigate} />
        ))}
      </Stack>
    </Box>
  );
}

function SidebarBrand() {
  const { View } = useLottie(
    {
      animationData: happyDog,
      loop: true,
      rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
    },
    { height: '100%', width: '100%' },
  );
  return <div style={{ height: 80, width: '100%' }}>{View}</div>;
}

function AccountProfile() {
  const { View } = useLottie(
    {
      animationData: profile,
      loop: true,
      rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
    },
    { height: '100%', width: '100%' },
  );
  return <div style={{ width: 36, height: 36, flexShrink: 0 }}>{View}</div>;
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { user, logout } = useAuth();
  return (
    <Stack h="100%" gap={0}>
      <SidebarBrand />
      <Box style={{ flex: 1, overflowY: 'auto' }} pt="md">
        <Stack gap="md">
          <Stack gap={4}>
            <SideLink {...ROUTES[0]} onNavigate={onNavigate} />
          </Stack>
          <BandGroup groupKey="content" onNavigate={onNavigate} />
          <BandGroup groupKey="resource" onNavigate={onNavigate} />
        </Stack>
      </Box>
      {user && (
        <Box
          p="sm"
          mt="sm"
          style={{ background: tokens.surface.band, borderRadius: 'var(--mantine-radius-lg)' }}
        >
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <AccountProfile />
              <Text size="sm" c={tokens.text.mid} truncate>
                {user.username} · {ROLE_LABELS[user.role]}
              </Text>
            </Group>
            <ActionIcon variant="subtle" color="gray" onClick={logout} aria-label="로그아웃">
              <IconLogout size={18} />
            </ActionIcon>
          </Group>
        </Box>
      )}
    </Stack>
  );
}

export function DashboardLayout() {
  const [opened, { toggle, close }] = useDisclosure(false);

  return (
    <AppShell
      navbar={{ width: 264, breakpoint: 'md', collapsed: { mobile: true } }}
      padding={{ base: 'md', md: 'lg' }}
    >
      <AppShell.Navbar
        p="md"
        style={{ background: tokens.surface.base, borderColor: tokens.border.default }}
      >
        <SidebarNav />
      </AppShell.Navbar>

      <AppShell.Main style={{ background: tokens.surface.base }}>
        <Burger opened={opened} onClick={toggle} hiddenFrom="md" size="sm" mb="md" />
        <Outlet />
      </AppShell.Main>

      <Drawer
        opened={opened}
        onClose={close}
        size={264}
        hiddenFrom="md"
        withCloseButton
        styles={{
          content: { background: tokens.surface.base },
          header: { background: tokens.surface.base },
          body: { height: '100%' },
        }}
      >
        <SidebarNav onNavigate={close} />
      </Drawer>
    </AppShell>
  );
}
